import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createBrowserHistory } from "history";
import SessionModel from "../../../api/models/SessionModel";
import { createTutorSession } from "../../../api/endpoints/tutors/sessions";
import {
  minutesToMilliseconds,
  openSessionInNewTab,
} from "../../../components/sessions/helper";
import { indexTutorSchedules } from "../../../api/endpoints/tutors/schedules";
import { showApiErrorMessages } from "../../../helpers";
import { listTelawahs } from "../../../api/endpoints/Telawahs/telawahs";
import { ApiResponse } from "../../../api/apiResponse";
import TelawahModel, { TelawahMap } from "../../../api/models/TelawahModel";
import { Hash } from "../../../api/@types/apiTypes";
import ApiDateParser from "../../../helpers/parsers/apiDateParser";
import { useQuery } from "react-query";
import ScheduledRoomCard, {
  ScheduledRoomCardStates,
} from "../../../components/sessions/RoomCards/ScheduledRoomCard";
import ActiveRoomCard from "../../../components/sessions/RoomCards/ActiveRoomCard";
import { FullSpinner } from "../../../components/LoadingSpinner";
import { adjustScheduleToCurrentTimezone } from "../../../services/schedulesParsingService";
import ScheduledSessionCollection from "../../../models/ScheduledSessionCollection";

type SessionFetchResult = {
  activeOrComingSession?: SessionModel;
  sessions: SessionModel[];
};

const REFETCH_SCHEDULE_INTERVAL = 5; // in minutes

export default function UpcommingSessions() {
  const { t, i18n } = useTranslation("", {
    keyPrefix: "page.tutor.dashboard.room",
  });
  const displayedScheduleDate = moment();

  const { data: userScheduledSessions, isLoading: isLoadingSchedule } =
    useQuery(
      ["getScheduleSlots"],
      async () => {
        const scheduleResponse = await indexTutorSchedules({
          filters: {
            skip_ended_session: true,
          },
        });
        if (!scheduleResponse.isSuccess) {
          console.error("error fetching schedule", scheduleResponse);
          showApiErrorMessages(scheduleResponse);
          return {};
        }

        const adjustedSchedule = adjustScheduleToCurrentTimezone(
          scheduleResponse.data
        );
        const todaysSlots = adjustedSchedule.getScheduledSessionsOnDate(
          displayedScheduleDate
        );
        return todaysSlots;
      },
      {
        refetchInterval: minutesToMilliseconds(REFETCH_SCHEDULE_INTERVAL),
      }
    );

  const {
    data: telawahs,
    isLoading: isLoadingTelawahs,
    refetch: refetchTelawahs,
  } = useQuery(
    ["getTelawahMap"],
    () => {
      return listTelawahs();
    },
    {
      refetchInterval: minutesToMilliseconds(REFETCH_SCHEDULE_INTERVAL),
      select: (response: ApiResponse) => {
        const data = TelawahModel.fromApiResponse(
          response.data
        ) as TelawahModel[];

        const map = TelawahModel.collectionToMap(data);
        return map;
      },
    }
  );

  const getAndSetSessions = useMemo((): SessionFetchResult => {
    let _scheduledSessions: ScheduledSessionCollection =
      (userScheduledSessions as ScheduledSessionCollection) ??
      new ScheduledSessionCollection([]);

    const groupedSessions = _scheduledSessions.createGroupsByDisplayStatus(
      displayedScheduleDate
    );

    const comingSession = groupedSessions.coming.collection
      .at(0)
      ?.toSessionModel();
    const inProgressSession = groupedSessions.in_progress.collection
      .at(0)
      ?.toSessionModel();
    let scheduledSessions = groupedSessions.scheduled.toSessionModel();

    scheduledSessions = scheduledSessions.filter(
      (session) =>
        !session.isReportedCancelledOrAbsence &&
        !session.isCanceled &&
        !session.isPastPermittedStartTimeWindow
    );
    // If there is a session in progress we give it the priority to be on the top of the page so that the user can enter it; And we put the upcoming session with the scheduled ones.
    if (!!inProgressSession) {
      return {
        activeOrComingSession: inProgressSession,
        sessions: !!comingSession
          ? [comingSession, ...scheduledSessions]
          : scheduledSessions,
      };
    } else if (!!comingSession) {
      return {
        activeOrComingSession: comingSession,
        sessions: scheduledSessions,
      };
    } else {
      return {
        sessions: scheduledSessions,
      };
    }
  }, [userScheduledSessions]);

  useEffect(() => {
    refetchTelawahs();
  }, [i18n.language]);

  if (isLoadingSchedule || isLoadingTelawahs) {
    return <FullSpinner size={100} />;
  }

  return (
    <section
      aria-labelledby="primary-heading"
      className="flex flex-col flex-1 min-h-full h-fit bg-gray-50"
    >
      <h1 className="p-6 text-2xl font-semibold text-gray-900 border-b border-gray-200 ">
        {t("readingRoom")}
      </h1>
      <main className="relative flex flex-col gap-16 py-16">
        <SessionsList
          telawahs={telawahs!}
          groupedSessions={getAndSetSessions}
        />
      </main>
    </section>
  );
}

type SessionsListProps = {
  telawahs: TelawahMap;
  groupedSessions: SessionFetchResult;
};

function SessionsList(props: SessionsListProps) {
  const { telawahs, groupedSessions } = props;
  let activeSession = groupedSessions.activeOrComingSession;
  let upcomingSessions = groupedSessions.sessions;
  if (!activeSession && upcomingSessions.length === 0) {
    return <EmptySessionsPlaceHolder />;
  }

  return (
    <>
      {!!activeSession && (
        <ActiveSessionsList session={activeSession} telawahs={telawahs} />
      )}
      {upcomingSessions.length > 0 && (
        <UpcomingSessionsList sessions={upcomingSessions} telawahs={telawahs} />
      )}
    </>
  );
}

type UpcomingSessionsListProps = {
  sessions: SessionModel[];
  telawahs: TelawahMap;
};

function UpcomingSessionsList(props: UpcomingSessionsListProps) {
  const { t } = useTranslation("", {
    keyPrefix: "page.tutor.dashboard.room",
  });

  const { sessions, telawahs } = props;
  // The following is done to set the telawah name on the session object itself
  sessions.forEach((session) =>
    session.setTelawahNameFromMapByStudent(telawahs)
  );

    const sortedSessions = [...sessions].sort((a, b) => {
      return moment(a.date).diff(moment(b.date));
    });
 
  // const sortedSessions = sessions.sort
  return (
    <div className="flex justify-center" data-cy="scheduledSessions">
      <div className="w-2/3">
        <div className="flex flex-col sm:flex-row">
          <h3 className="w-full text-lg font-medium text-gray-700">
            {t("scheduledSessions")}
          </h3>
          <div className="flex items-center w-auto text-xs font-normal text-brandblue-600 sm:min-w-fit">
            {moment().format("dddd D/M/YYYY")}
          </div>
        </div>
        {sortedSessions.map((session, idx) => {
          return (
            <div className="mt-4" key={`RoomCard-${session.id}-${idx}`}>
              <ScheduledRoomCard
                participantName={session.student!.name}
                readingProgress={session.student!.lastQuranPageReached}
                sessionNumber={session.sessionNumber || 0}
                sessionStartTime={session.start_time!}
                telawahName={telawahs[`${session.student!.telawah_id}`]}
                profilePictureUrl={session.student!.profile_picture_url}
                sessionStatusText={t(session.statusReported)}
                state={ScheduledRoomCardStates.inactive}
                isEvaluationSession={!!session.student!.isLearner}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

type ActiveSessionsListProps = {
  session: SessionModel;
  telawahs: TelawahMap;
};

function ActiveSessionsList(props: ActiveSessionsListProps) {
  const { t } = useTranslation("", {
    keyPrefix: "page.tutor.dashboard.room",
  });
  const history = createBrowserHistory();

  const { session, telawahs } = props;
  // The following is done to set the telawah name on the session object itself
  session.setTelawahNameFromMapByStudent(telawahs);

  const createUserSession = async (session: SessionModel) => {
    if (session.isInProgress) {
      openSessionInNewTab("tutor")(session.id!);
      return;
    }
    const startTime = ApiDateParser.normalize(
      ApiDateParser.toApiDate(session.start_time!)
    );
    const sessionData: Hash<any> = {
      date: session.date,
      start_time: startTime,
    };
    if (session.isCandidateSession) {
      sessionData["candidate_id"] = session.student?.id;
      sessionData["schedule_slot_id"] = session.slotId;
    } else {
      sessionData["student_id"] = session.student?.id;
      sessionData["schedule_slot_id"] = session.slotId;
    }

    const apiResponse = await createTutorSession(sessionData);
    if (apiResponse.isSuccess) {
      openSessionInNewTab("tutor")(apiResponse.data.id!);
      // reload the page
      history.go(0);
    } else {
      showApiErrorMessages(apiResponse);
    }
  };

  const handleStartSession = (session: SessionModel) => {
    createUserSession(session);
  };

  return (
    <div className="flex justify-center" data-cy="inComingSessions">
      <div className="w-2/3">
        <div className="flex flex-col sm:flex-row">
          <h3
            className="w-full text-lg font-medium text-gray-700"
            data-cy="activeOrComingSessionLabel"
          >
            {t("in_progress")}
          </h3>
        </div>
        <div className="mt-4">
          <ActiveRoomCard session={session} onEnter={handleStartSession} />
        </div>
      </div>
    </div>
  );
}

function EmptySessionsPlaceHolder() {
  const { t } = useTranslation("", {
    keyPrefix: "page.tutor.dashboard.room",
  });
  return (
    <div data-cy="emptySessionsPlaceholder">
      <div className="text-center">
        <p className="text-lg font-medium text-gray-700">{t("noReaders")}</p>
        <p className="mt-1 text-sm font-normal text-gray-500">
          {t("takeSomeRest")}
        </p>
        <div className="mt-6 text-sm font-medium text-brandblue-600">
          <a href="#">{t("RoomEtiquette")}</a>
        </div>
      </div>
    </div>
  );
}
